import React from 'react';
import { Tag } from '@maxtropy/components';
import titleIcon from '@/assets/images/title_icon.png';
import { MEDIUMS } from '../MediumToolbar';
import type { AreaLoadMatrixRow, AreaOverview, LoadRangeKey } from '../../types';
import styles from './index.module.scss';

interface AreaMatrixSectionProps {
  overview: AreaOverview;
  rows: AreaLoadMatrixRow[];
}

const RANGE_CONFIG: { key: LoadRangeKey; label: string; color: string; background: string; border: string }[] = [
  {
    key: 'low',
    label: '0~60%',
    color: '#16DD8E',
    background: 'rgba(22,221,142,0.2)',
    border: 'rgba(22,221,142,0.4)',
  },
  {
    key: 'mid',
    label: '60~80%',
    color: '#FFCB47',
    background: 'rgba(255,203,71,0.2)',
    border: 'rgba(255,203,71,0.4)',
  },
  {
    key: 'high',
    label: '80%以上',
    color: '#FF9247',
    background: 'rgba(255,146,71,0.2)',
    border: 'rgba(255,146,71,0.4)',
  },
];

const mediumIconMap: Map<number, string> = new Map(MEDIUMS.map(item => [item.value, item.icon]));

const getRankClass = (index: number) => {
  if (index === 0) return styles.rankFirst;
  if (index === 1) return styles.rankSecond;
  if (index === 2) return styles.rankThird;
  return styles.rankNormal;
};

const renderRank = (index: number) => (
  <span className={getRankClass(index)}>
    <span>{index + 1}</span>
  </span>
);

const AreaMatrixSection: React.FC<AreaMatrixSectionProps> = ({ overview, rows }) => {
  const tableRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const updateScrollbarWidth = () => {
      const body = bodyRef.current;
      const table = tableRef.current;
      if (!body || !table) return;
      table.style.setProperty('--body-scrollbar-width', `${body.offsetWidth - body.clientWidth}px`);
    };
    updateScrollbarWidth();
    const resizeObserver = new ResizeObserver(updateScrollbarWidth);
    if (bodyRef.current) resizeObserver.observe(bodyRef.current);
    return () => resizeObserver.disconnect();
  }, [rows]);

  const handleBodyScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (headerRef.current) headerRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  return (
    <div className={styles.areaMatrix}>
      <div className={styles.overview}>
        <div className={styles.overviewCard}>
          <span>统计生产区域：</span>
          <span className={styles.totalValue}>{overview.totalAreas}</span>
        </div>
        <div className={styles.overviewCard}>
          <span>80%以上占比最高的区域数：</span>
          <span className={styles.highValue}>{overview.highAreaCount}</span>
          <span> 个高关注</span>
        </div>
        <div className={styles.overviewCard}>
          <div>
            <span className={styles.overviewTitle}>80%以上占比最高区域：</span>
            <span className={styles.overviewValue}>{overview.focusArea}</span>
          </div>
          <div>
            <span className={styles.overviewTitle}>持续时长：</span>
            <span className={styles.overviewValue}>{overview.focusDuration}小时</span>
          </div>
          <div>
            <span className={styles.overviewTitle}>占比：</span>
            <span className={styles.overviewValue}>{overview.focusRatio}%</span>
          </div>
          <div>
            <span className={styles.overviewTitle}>对应介质：</span>
            <span className={styles.overviewValue}>{overview.focusMedium}</span>
          </div>
        </div>
        <div className={styles.legendCard}>
          <span>负载率时长区间</span>
          {RANGE_CONFIG.map(item => (
            <span
              key={item.key}
              className={styles.legendItem}
              style={
                {
                  '--range-color': item.color,
                  '--range-bg': item.background,
                  '--range-border': item.border,
                } as React.CSSProperties
              }
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.matrixTitle}>
        <img className={styles.titleIcon} src={titleIcon} alt="" />
        <span>区域 × 气体介质</span>
        <span>不含电力；每个区域一行，各区域横向展示累计时长、占比和日均时长</span>
      </div>
      <div className={styles.table} ref={tableRef}>
        <div className={styles.headerViewport} ref={headerRef}>
          <div className={styles.header}>
            <div className={styles.areaHeader}>生产区域</div>
            {rows[0]?.stats.map(stat => (
              <div key={stat.mediumType} className={styles.mediumHeader}>
                <img src={mediumIconMap.get(stat.mediumType)} alt="" />
                <span>{stat.mediumName}</span>
                <span>三档负荷统计</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.bodyViewport} ref={bodyRef} onScroll={handleBodyScroll}>
          <div className={styles.body}>
            {rows.map((row, index) => (
              <div className={styles.row} key={row.key}>
                <div className={styles.areaCell}>
                  {renderRank(index)}
                  <div className={styles.areaInfo}>
                    <span>{row.name}</span>
                    <Tag>{row.tag}</Tag>
                  </div>
                </div>
                {row.stats.map(stat => (
                  <div key={`${row.key}-${stat.mediumType}`} className={styles.statCell}>
                    {RANGE_CONFIG.map(range => {
                      const value = stat.ranges[range.key];
                      return (
                        <div
                          key={range.key}
                          className={styles.rangeLine}
                          style={{ '--range-color': range.color } as React.CSSProperties}
                        >
                          <span className={styles.percent}>{value.percent.toFixed(1)}%</span>
                          <span className={styles.track}>
                            <span className={styles.bar} style={{ width: `${value.percent}%` }} />
                          </span>
                          <span className={styles.duration}>{value.duration}小时</span>
                          <span className={styles.average}>日均 {value.dailyAverage.toFixed(1)}小时</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaMatrixSection;
